// WCAG AA contrast utilities. The admin Colors tab uses these to warn a
// non-designer owner before they ship white-on-yellow.
//
// Inputs are HSL triplets matching the `site_branding` convention — e.g.
// "129 26% 22%" (no hsl() wrapper, space-separated, percent on S/L).

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHslTriplet(s: string): { h: number; s: number; l: number } | null {
  // "H S% L%" — percent sign on S and L optional for robustness
  const m = s.trim().match(/^([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)%?\s+([+-]?\d+(?:\.\d+)?)%?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const s2 = Number(m[2]);
  const l = Number(m[3]);
  if (Number.isNaN(h) || Number.isNaN(s2) || Number.isNaN(l)) return null;
  return { h, s: s2, l };
}

export function hslToRgb(h: number, s: number, l: number): Rgb {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = ln - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function channelLuminance(ch: number): number {
  const v = ch / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

export function hslTripletToRgb(triplet: string): Rgb | null {
  const parsed = parseHslTriplet(triplet);
  if (!parsed) return null;
  return hslToRgb(parsed.h, parsed.s, parsed.l);
}

export type WcagLevel = "AAA" | "AA" | "AA-large" | "fail";

export function wcagLevel(ratio: number): WcagLevel {
  if (ratio >= 7) return "AAA";
  if (ratio >= 4.5) return "AA";
  if (ratio >= 3) return "AA-large";
  return "fail";
}
